import { Suspense, lazy } from 'react';

import { Permission } from '@bookora/shared';
import { PageFallback } from '@bookora/ui';
import type { RouteObject } from 'react-router-dom';

import { RequirePermission } from '@/app/require-permission';
import { RouteErrorBoundary } from '@/components/route-error-boundary';

/**
 * The patient module's child routes.
 *
 * `/patients` itself is contributed by `app/sections.ts` (it is a NAVIGATION
 * destination); these four are not, which is why they live here. `router.tsx`
 * spreads this array into `EXTRA_ROUTES`.
 *
 * Three things this file is responsible for, per the contract at the top of
 * `router.tsx`:
 *
 *  1. **Its own permission gate.** The section wrapper around `/patients` does
 *     not extend to sibling paths, so each route states what it needs.
 *     `patient.view` for reading, `patient.create` for the new-file form,
 *     `patient.edit` for the edit form, and `patient.view` + `patient.delete`
 *     for the recycle bin — the same pairs the endpoints behind them require.
 *     These are affordances; every request is checked again on the server.
 *  2. **Its own `errorElement`, on each LEAF.** Attached to the layout instead,
 *     a thrown error would blank the sidebar and the clinic switcher along with
 *     the screen.
 *  3. **Lazy chunks.** The patient file pulls in the notes composer, the
 *     timeline and the Jalali picker; none of that belongs in the shell's
 *     initial bundle on an Iranian connection.
 *
 * Route ORDER does not decide the match — React Router ranks by specificity, so
 * `/patients/new` beats `/patients/:id` wherever it sits. It is written
 * static-first anyway, because the reader should not have to know that.
 */

const NewPatientPage = lazy(() => import('./patient-new-page'));
const EditPatientPage = lazy(() => import('./patient-edit-page'));
const RecycleBinPage = lazy(() => import('./recycle-bin-page'));
const PatientDetailPage = lazy(() => import('./patient-detail-page'));
const PatientImportPage = lazy(() => import('./patient-import-page'));

function guarded(anyOf: readonly Permission[], element: React.ReactNode): React.ReactElement {
  return (
    <RequirePermission anyOf={anyOf}>
      <Suspense fallback={<PageFallback />}>{element}</Suspense>
    </RequirePermission>
  );
}

export const routes: RouteObject[] = [
  {
    path: 'patients/new',
    element: guarded([Permission.PATIENT_CREATE], <NewPatientPage />),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: 'patients/recycle-bin',
    // `RequirePermission` is an ANY-of gate, and the endpoint wants BOTH keys.
    // Gating on `patient.delete` is the narrower of the two and the one that
    // actually distinguishes a role: nobody holds it without `patient.view`.
    element: guarded([Permission.PATIENT_DELETE], <RecycleBinPage />),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: 'patients/:id/edit',
    element: guarded([Permission.PATIENT_EDIT], <EditPatientPage />),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: 'patients/:id',
    element: guarded([Permission.PATIENT_VIEW], <PatientDetailPage />),
    errorElement: <RouteErrorBoundary />,
  },
  {
    // The onboarding checklist's «ثبت فهرست بیماران» step hands out
    // `/patients/import`. A standalone page around the same self-contained
    // `PatientImportPanel` the راه‌اندازی tab shows inline — a redirect back to
    // that tab just looked like a no-op refresh.
    path: 'patients/import',
    element: guarded([Permission.PATIENT_CREATE], <PatientImportPage />),
    errorElement: <RouteErrorBoundary />,
  },
];

export default routes;
